import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { CirclePlus, Search } from 'lucide-react';
import FormFieldComponent from '@/components/FormFieldComponent';
import { FormFieldSelect } from '@/components/FormFieldSelect';
import FormFieldCheckbox from '@/components/FormFieldCheckbox';
import FormFieldCalendar from '@/components/FormFieldCalendar';
import FormFieldTextArea from '@/components/FormFieldTextArea';
import FormFieldSearch from '@/components/FormFieldSearch';
import { Button } from '@/components/ui/button';
import { ADDRESS_LINE_VALIDATION, CITY_VALIDATION, STATE_VALIDATION } from '@/utils/constant';
import { useDdiReports } from '@/services/dueDiligenceService';
import { useTeamGetAll } from '@/services/teamService';
import { getUsersByTeam } from '@/services/Network';
import type { TeamGetAllParams } from '@/types/team.types';
import {
  DDI_ADDRESS_TYPE_OPTIONS,
  estimateDueDateFromRushOrType,
  formatDdiReportOptionLabel,
  isAddressApplicable,
  requiresAddressType,
  requiresEntityInquiry,
  requiresVin,
  type DdiCreateFormValues,
} from './ddiWorkOrderCreate.schema';

const SearchIcon = {
  Icon: Search,
  iconProps: { className: 'text-[#969696] h-5 w-5' },
};

type DDIWorkOrderCreateStep2Props = {
  /** Admin/supervisor + assign feature — mirrors API assign-other permission. */
  showAssignedUserField?: boolean;
};

export const DDIWorkOrderCreateStep2 = ({ showAssignedUserField = false }: DDIWorkOrderCreateStep2Props) => {
  const { control, setValue, watch } = useFormContext<DdiCreateFormValues>();
  const ddiType = watch('ddiType') ?? '';
  const ddiTypeId = watch('ddiTypeId');
  const ddiSubtypeId = watch('ddiSubtypeId');
  const showAlternativeAddress = watch('showAlternativeAddress');

  const showEntity = requiresEntityInquiry(ddiType);
  const showVin = requiresVin(ddiType);
  const showAddress = isAddressApplicable(ddiType);
  const showAddressType = requiresAddressType(ddiType);
  const showPerson = !showEntity;
  const personSharesRow = showAddressType || showVin;

  const { data: teamsData } = useTeamGetAll();
  const ddiTeamId = teamsData?.teams?.find((team) => team.key === 'DUE_DILIGENCE')?.id;

  const { data: reports = [] } = useDdiReports(ddiTypeId ?? undefined, ddiSubtypeId ?? null);
  const reportOptions = useMemo(
    () => reports.map((r) => ({ label: formatDdiReportOptionLabel(r.name), value: r.id })),
    [reports]
  );

  const applyRushDueDate = (sameDayRush: boolean, nextDayRush: boolean) => {
    setValue('dueDate', estimateDueDateFromRushOrType(ddiType, { sameDayRush, nextDayRush }), {
      shouldValidate: true,
    });
    // Rush-driven dates should be submitted; clearing rush restores the type estimate and
    // clears touched so the backend can recalculate when dueDate is omitted.
    setValue('dueDateTouched', sameDayRush || nextDayRush);
  };

  const handleSameDayRush = (checked: boolean) => {
    setValue('sameDayRush', checked, { shouldValidate: true });
    if (checked) setValue('nextDayRush', false, { shouldValidate: true });
    applyRushDueDate(checked, false);
  };

  const handleNextDayRush = (checked: boolean) => {
    setValue('nextDayRush', checked, { shouldValidate: true });
    if (checked) setValue('sameDayRush', false, { shouldValidate: true });
    applyRushDueDate(false, checked);
  };

  const clearAlternativeAddress = () => {
    setValue('showAlternativeAddress', false);
    setValue('alternativeAddressLine1', null);
    setValue('alternativeAddressLine2', null);
    setValue('alternativeAddressCity', null);
    setValue('alternativeAddressState', null);
    setValue('alternativeAddressZipCode', null);
  };

  return (
    <div className="flex flex-col w-full mt-11 gap-6">
      {showEntity && (
        <div className="grid grid-cols-1 gap-6 text-black">
          <FormFieldComponent
            control={control}
            name="entityForInquiry"
            label="Entity for Inquiry"
            placeholder="Entity for Inquiry"
            required
          />
        </div>
      )}

      {showPerson && (
        <div
          className={`grid grid-cols-1 gap-6 text-black ${personSharesRow ? 'md:grid-cols-2' : ''}`}
        >
          <FormFieldComponent
            control={control}
            name="personForInquiry"
            label="Person for Inquiry"
            placeholder="Person for Inquiry"
            required
          />
          {showAddressType && (
            <FormFieldSelect
              name="addressType"
              options={DDI_ADDRESS_TYPE_OPTIONS}
              placeholder="Select Address Type"
              label="Address Type"
              required
            />
          )}
          {showVin && (
            <FormFieldComponent
              control={control}
              name="vin"
              label="Vehicle Identification Number (VIN)"
              placeholder="Vehicle Identification Number (VIN)"
              required
              maxLength={17}
            />
          )}
        </div>
      )}

      {showAddress && (
        <>
          <div className="grid grid-cols-1 gap-6 text-black">
            <FormFieldComponent
              control={control}
              name="addressLine1"
              label="Address Line 1"
              placeholder="Address Line 1"
              required
              {...ADDRESS_LINE_VALIDATION}
            />
            <FormFieldComponent
              control={control}
              name="addressLine2"
              label="Address Line 2"
              placeholder="Address Line 2"
              {...ADDRESS_LINE_VALIDATION}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-black">
            <FormFieldComponent
              control={control}
              name="city"
              label="City"
              placeholder="City"
              required
              {...CITY_VALIDATION}
            />
            <FormFieldComponent
              control={control}
              name="state"
              label="State"
              placeholder="State"
              required
              {...STATE_VALIDATION}
            />
            <FormFieldComponent
              control={control}
              name="zipCode"
              label="Zip Code"
              placeholder="Zip Code"
              required
            />
          </div>

          {!showAlternativeAddress ? (
            <div className="flex justify-end">
              <button
                type="button"
                className="flex w-fit items-center gap-2 text-sm font-normal text-black hover:bg-[#EDF5FF] rounded px-2 py-1"
                onClick={() => setValue('showAlternativeAddress', true)}
              >
                <CirclePlus className="h-4 w-4" color="#1B8652" />
                Add Alternative Address
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-6 border border-[#E5E5E5] rounded-md p-4">
              <div className="flex justify-between items-center">
                <p className="text-sm font-medium text-[#0A2240]">Alternative Address</p>
                <Button type="button" variant="ghost" className="text-[#676767]" onClick={clearAlternativeAddress}>
                  Remove
                </Button>
              </div>
              <div className="grid grid-cols-1 gap-6 text-black">
                <FormFieldComponent
                  control={control}
                  name="alternativeAddressLine1"
                  label="Alternative Address Line 1"
                  placeholder="Address Line 1"
                  {...ADDRESS_LINE_VALIDATION}
                />
                <FormFieldComponent
                  control={control}
                  name="alternativeAddressLine2"
                  label="Alternative Address Line 2"
                  placeholder="Address Line 2"
                  {...ADDRESS_LINE_VALIDATION}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-black">
                <FormFieldComponent
                  control={control}
                  name="alternativeAddressCity"
                  label="City"
                  placeholder="City"
                  {...CITY_VALIDATION}
                />
                <FormFieldComponent
                  control={control}
                  name="alternativeAddressState"
                  label="State"
                  placeholder="State"
                  {...STATE_VALIDATION}
                />
                <FormFieldComponent
                  control={control}
                  name="alternativeAddressZipCode"
                  label="Zip Code"
                  placeholder="Zip Code"
                />
              </div>
            </div>
          )}
        </>
      )}

      <div className="grid grid-cols-1 gap-6 text-black">
        <FormFieldTextArea name="notes" label="Notes" placeholder="Notes" />
      </div>

      {showAssignedUserField && ddiTeamId && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-black">
          <FormFieldSearch<TeamGetAllParams>
            name="assignedUserId"
            label="Assigned User"
            placeholder="Search User"
            props={SearchIcon}
            searchFieldName="userName"
            api={(params) =>
              getUsersByTeam(
                {
                  teamId: ddiTeamId,
                  userName: params.userName ?? undefined,
                  userStatus: 'ACTIVE',
                },
                true
              )
            }
            onBlur={(option) => {
              if (!option) setValue('assignedUserId', null, { shouldDirty: true });
            }}
          />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-black">
        <FormFieldCalendar
          control={control}
          name="dueDate"
          label="Due Date"
          placeholder="Due Date"
          clearable
          onChange={() => setValue('dueDateTouched', true)}
        />
        {ddiTypeId && (
          <FormFieldSelect
            name="reportId"
            options={reportOptions}
            placeholder="Select Report"
            label="Report"
            clearable
          />
        )}
      </div>

      <div className="flex flex-row gap-6 flex-wrap">
        <FormFieldCheckbox
          control={control}
          name="sameDayRush"
          label="Same Day Rush"
          onCheckChange={handleSameDayRush}
        />
        <FormFieldCheckbox
          control={control}
          name="nextDayRush"
          label="Next Day Rush"
          onCheckChange={handleNextDayRush}
        />
      </div>
    </div>
  );
};
